import { Box } from '@mui/material';
import { MemoMultiAutocomplete } from 'components/common/simpleInputFields/multichoice/multiAutocomplete';
import { createLabelLocalizer } from 'helpers/misc';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { findById } from 'shared/utils/id/findById';
import { ChartFilter } from 'shared/types/analytics';
import { LabelledEntity } from 'shared/types/commonView';
import { ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

export function MultiselectControl(props: {
  filter: ChartFilter<unknown>;
  localSetter: (value: string[]) => void;
  localValue: string[];
  disabled: boolean;
  openOnMount?: boolean;
}): ReactElement {
  const intl = useIntl();
  const { labelId, available, labels } = props.filter;
  const localSetter = props.localSetter;
  const value = props.localValue;

  const getLocalizedLabel = useMemo(
    () => createLabelLocalizer(intl, labels),
    [intl, labels]
  );

  const localizedAvailable = useMemo(
    () =>
      available?.filter(keepUndeleted).map((item) => ({
        _id: item._id,
        label: getLocalizedLabel(item._id, item.label),
      })) ?? [],
    [available, getLocalizedLabel]
  );

  const selectedValues = useMemo(
    () =>
      value.map((val) => {
        const item = findById(available, val, 'MultiselectControl.selectedValues');
        return {
          _id: val,
          label: getLocalizedLabel(val, item?.label),
        };
      }),
    [value, getLocalizedLabel, available]
  );

  const handleChange = useCallback(
    (_: unknown, newValue: LabelledEntity[]) => {
      localSetter(newValue.map((item) => item._id));
    },
    [localSetter]
  );

  return (
    <Box sx={{ width: '100%', gridColumn: '1 / -1' }}>
      <MemoMultiAutocomplete
        available={localizedAvailable}
        value={selectedValues}
        handleChange={handleChange}
        localisedLabel={formatTranslation(intl, unsafeTranslationKey(labelId))}
        required={false}
        error=""
        id={`chart-filter-${labelId}`}
        disableClearable={false}
        dense={false}
        reserveSpaceForHelperText={false}
        ref={null}
        disabled={props.disabled}
        size="medium"
        maxVisibleTags={3}
        openOnMount={props.openOnMount}
      />
    </Box>
  );
}
